/**
 * Service for fetching allergies from NLM Clinical Tables API
 * API Documentation: https://clinicaltables.nlm.nih.gov/
 * 
 * Note: NLM Clinical Tables API doesn't have a dedicated allergies endpoint.
 * This service tries multiple endpoints and falls back to a comprehensive list of common allergens.
 */

export interface AllergyOption {
  value: string;
  label: string;
}

/**
 * Comprehensive list of common allergens as fallback
 */
const COMMON_ALLERGENS: string[] = [
  // Drug Allergies
  'Penicillin', 'Amoxicillin', 'Ampicillin', 'Cephalexin', 'Ciprofloxacin', 'Sulfonamides',
  'Aspirin', 'Ibuprofen', 'Naproxen', 'Codeine', 'Morphine', 'Vancomycin',
  'Insulin', 'Latex', 'Contrast Dye', 'Anesthesia',
  
  // Food Allergies
  'Peanuts', 'Tree Nuts', 'Almonds', 'Walnuts', 'Cashews', 'Pistachios', 'Hazelnuts',
  'Milk', 'Eggs', 'Soy', 'Wheat', 'Gluten', 'Fish', 'Shellfish', 'Shrimp', 'Crab', 'Lobster',
  'Sesame', 'Mustard', 'Sulfites',
  
  // Environmental Allergies
  'Pollen', 'Grass', 'Ragweed', 'Dust Mites', 'Mold', 'Pet Dander', 'Cat Dander', 'Dog Dander',
  'Bee Sting', 'Wasp Sting', 'Fire Ants',
  
  // Other Common Allergens
  'Nickel', 'Fragrance', 'Formaldehyde', 'Parabens', 'Neomycin', 'Bacitracin',
];

/**
 * Filters common allergens based on search term
 */
const filterCommonAllergens = (searchTerm: string): AllergyOption[] => {
  if (!searchTerm.trim()) {
    return COMMON_ALLERGENS.map(allergen => ({
      value: allergen,
      label: allergen,
    }));
  }
  
  const term = searchTerm.toLowerCase();
  return COMMON_ALLERGENS
    .filter(allergen => allergen.toLowerCase().includes(term))
    .map(allergen => ({
      value: allergen,
      label: allergen,
    }));
};

/**
 * Fetches allergies from NLM Clinical Tables API based on search term
 * Tries multiple endpoints: conditions (for allergic conditions), rxterms (for drug allergies)
 * Falls back to common allergens list if API calls fail
 * @param searchTerm - The search term to query allergies
 * @returns Promise with array of allergy options
 */
/**
 * Parses NLM API response to extract allergy/condition terms
 * Handles response format: [totalCount, [codes], null, [[term1], [term2], ...]]
 */
const parseNLMResponse = (data: unknown): AllergyOption[] => {
  if (!Array.isArray(data) || data.length < 4) {
    return [];
  }

  // Check if index 3 contains the terms array
  const termsArray = data[3];
  if (!Array.isArray(termsArray)) {
    return [];
  }

  // Extract terms from nested arrays
  const terms = termsArray
    .map((termGroup) => {
      if (Array.isArray(termGroup) && termGroup.length > 0) {
        return termGroup[0] as string;
      }
      return null;
    })
    .filter((term): term is string => term !== null && term.trim().length > 0);

  // Remove duplicates and format
  const uniqueTerms = Array.from(new Set(terms));
  return uniqueTerms.map((term) => ({
    value: term.trim(),
    label: term.trim(),
  }));
};

export const fetchAllergies = async (searchTerm: string = ''): Promise<AllergyOption[]> => {
  const baseUrl = 'https://clinicaltables.nlm.nih.gov';
  
  // Try NLM Clinical Tables API endpoints
  const endpoints = [
    // Try rxterms API for drug allergies (with ef parameter for additional fields)
    {
      url: `${baseUrl}/api/rxterms/v3/search?ef=STRENGTHS_AND_FORMS,RXCUIS&terms=${encodeURIComponent(searchTerm)}`,
      type: 'rxterms',
    },
    // Try conditions API for allergic conditions
    {
      url: `${baseUrl}/api/conditions/v3/search?terms=${encodeURIComponent(searchTerm || 'allergy')}`,
      type: 'conditions',
    },
  ];

  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint.url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const results = parseNLMResponse(data);
        
        // If we got results, return them (possibly combined with common allergens)
        if (results.length > 0) {
          // Combine API results with common allergens, removing duplicates
          const apiValues = new Set(results.map(r => r.value.toLowerCase()));
          const commonAllergens = filterCommonAllergens(searchTerm).filter(
            allergen => !apiValues.has(allergen.value.toLowerCase())
          );
          return [...results, ...commonAllergens].slice(0, 100); // Limit to 100 results
        }
      }
    } catch (error) {
      // Continue to next endpoint or fallback
      console.debug(`Failed to fetch from ${endpoint.url}:`, error);
    }
  }
  
  // Fallback to common allergens list
  return filterCommonAllergens(searchTerm);
};

/**
 * Fetches initial list of allergies (without search term)
 * This can be used to populate initial options
 */
export const fetchInitialAllergies = async (limit: number = 100): Promise<AllergyOption[]> => {
  // Use the main fetch function with empty search term
  const results = await fetchAllergies('');
  return results.slice(0, limit);
};

